import { useState } from 'react'
import type { JSX } from 'react'

import {
  EXPENSE_ALLOCATIONS,
  EXPENSE_CATEGORIES,
  type ExpenseAllocation,
  type ExpenseCategory,
} from '@lensmira/domain'
import { formatDay, type MessageKey } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { formatWireForDisplay, MoneyInputError, parseMajorInput } from '../money.js'
import type { WireMoney } from '../money.js'
import { connectionLine, writeLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * What the shop spent that is not stock. `BR-11`, Appendix B `expenses_record`.
 *
 * One form and one list. The write goes through the queue (`OFF-001a`) to `POST /expenses`.
 *
 * **Everything the server would refuse is met here first.** A refused write is `blocked`, a blocked
 * head halts the queue, and the queue carries payments. So the amount, the date, the note length and
 * the one refusal that depends on the world (paying from a drawer that is not open) are all checked
 * before anything is queued, with the server's own limits mirrored.
 *
 * **Paid from the drawer also takes the money out of the drawer**, in the same server transaction,
 * so the cash expected at close drops by what was spent. The screen says so beside the choice. The
 * drawer's state arrives as `drawerOpen`, because this screen has no business reading the cash
 * session itself, and an expense is never queued against a drawer the parent says is closed.
 */

export interface ExpenseView {
  readonly id: string
  readonly category: ExpenseCategory
  readonly allocation: ExpenseAllocation
  /** Null for a shared cost. */
  readonly branchId: string | null
  readonly amount: WireMoney
  readonly paidFrom: 'cash' | 'bank' | 'accounts_payable'
  readonly incurredOn: string
  readonly note: string | null
}

export interface ExpensesProps {
  /** The branch this till is at. */
  readonly branchId: string
  readonly terminalId: string
  /** Currency of the amount field, which decides its decimal places (`MR-01`). */
  readonly currency: string
  /** The branch's zone, for writing the day a cost was incurred the way the shop writes dates. */
  readonly timezone: string
  /** Whether this terminal has an open cash session. Cash is not offered as a source without one. */
  readonly drawerOpen: boolean
  readonly expenses: readonly ExpenseView[]
  /** Called once the server has taken the write, so the parent can re-read the list. */
  readonly onSaved?: () => void
}

const NOTE_MAX = 500
const BIGINT_MAX = 9_223_372_036_854_775_807n

const CATEGORY_LABEL: Readonly<Record<ExpenseCategory, MessageKey>> = {
  rent: 'expenses.category.rent',
  salaries: 'expenses.category.salaries',
  utilities: 'expenses.category.utilities',
  marketing: 'expenses.category.marketing',
  equipment: 'expenses.category.equipment',
  professional_fees: 'expenses.category.professional_fees',
  transport: 'expenses.category.transport',
  maintenance: 'expenses.category.maintenance',
  bank_charges: 'expenses.category.bank_charges',
  other: 'expenses.category.other',
}

const ALLOCATION_LABEL: Readonly<Record<ExpenseAllocation, MessageKey>> = {
  direct: 'expenses.allocation.direct',
  by_revenue: 'expenses.allocation.by_revenue',
  equal: 'expenses.allocation.equal',
}

const PAID_FROM = ['cash', 'bank', 'accounts_payable'] as const
type PaidFrom = (typeof PAID_FROM)[number]

const PAID_FROM_LABEL: Readonly<Record<PaidFrom, MessageKey>> = {
  cash: 'expenses.paidFrom.cash',
  bank: 'expenses.paidFrom.bank',
  accounts_payable: 'expenses.paidFrom.accounts_payable',
}

/** A real calendar date, the server's `requireCalendarDate` rule. */
function isCalendarDate(text: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
  if (!match) return false
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const probe = new Date(Date.UTC(year, month - 1, day))
  return (
    year >= 1900 &&
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  )
}

export function Expenses({
  branchId,
  terminalId,
  currency,
  timezone,
  drawerOpen,
  expenses,
  onSaved,
}: ExpensesProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()

  const [category, setCategory] = useState<ExpenseCategory>('other')
  const [allocation, setAllocation] = useState<ExpenseAllocation>('direct')
  const [paidFrom, setPaidFrom] = useState<PaidFrom>('bank')
  const [amountInput, setAmountInput] = useState('')
  const [dateInput, setDateInput] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  useWriteSettled(snapshot, writeId, () => {
    onSaved?.()
  })

  const submit = async (): Promise<void> => {
    let amount: bigint
    try {
      amount = parseMajorInput(amountInput, currency)
    } catch (caught) {
      if (!(caught instanceof MoneyInputError)) throw caught
      setError(t('expenses.amountRequired'))
      return
    }
    if (amount <= 0n || amount > BIGINT_MAX) {
      setError(t('expenses.amountRequired'))
      return
    }
    const date = dateInput.trim()
    if (date !== '' && !isCalendarDate(date)) {
      setError(t('expenses.dateInvalid'))
      return
    }
    if (note.trim().length > NOTE_MAX) {
      setError(t('expenses.noteTooLong', { max: NOTE_MAX }))
      return
    }
    // The one refusal that depends on the world. Queued, it would be blocked and stop the till.
    if (paidFrom === 'cash' && !drawerOpen) {
      setError(t('expenses.noDrawer'))
      return
    }
    setError(null)

    const write = (await enqueue({
      method: 'POST',
      path: '/expenses',
      label: t('expenses.queueLabel', { category: t(CATEGORY_LABEL[category]) }),
      body: {
        branchId,
        category,
        allocation,
        amountMinor: amount.toString(),
        paidFrom,
        ...(date === '' ? {} : { incurredOn: date }),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        ...(paidFrom === 'cash' ? { terminalId } : {}),
      },
    })) as { id: string }
    setWriteId(write.id)
    setAmountInput('')
    setNote('')
  }

  const line = writeLine(t, snapshot, writeId, t('queue.subject.expense'))

  return (
    <section className="lm-screen" aria-labelledby="expenses-heading">
      <h1 id="expenses-heading" className="lm-screen__heading">
        {t('expenses.heading')}
      </h1>
      <p className="lm-queue-state" role="status">
        {connectionLine(t, snapshot)}
      </p>

      <form
        className="lm-form"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <h2 className="lm-screen__subheading">{t('expenses.recordHeading')}</h2>

        <label className="lm-field">
          <span className="lm-field__label">{t('expenses.categoryLabel')}</span>
          <select
            className="lm-input"
            name="category"
            value={category}
            onChange={(e) => {
              setCategory(e.currentTarget.value as ExpenseCategory)
            }}
          >
            {EXPENSE_CATEGORIES.map((id) => (
              <option key={id} value={id}>
                {t(CATEGORY_LABEL[id])}
              </option>
            ))}
          </select>
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('expenses.amountLabel')}</span>
          <input
            className="lm-input"
            name="amount"
            inputMode="decimal"
            autoComplete="off"
            value={amountInput}
            onChange={(e) => {
              setAmountInput(e.currentTarget.value)
            }}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('expenses.paidFromLabel')}</span>
          <select
            className="lm-input"
            name="paidFrom"
            value={paidFrom}
            onChange={(e) => {
              setPaidFrom(e.currentTarget.value as PaidFrom)
            }}
          >
            {PAID_FROM.map((id) => (
              <option key={id} value={id}>
                {t(PAID_FROM_LABEL[id])}
              </option>
            ))}
          </select>
        </label>
        {paidFrom === 'cash' ? (
          <p className="lm-hint" data-testid="expenses-cash-means">
            {drawerOpen ? t('expenses.cashMeans') : t('expenses.noDrawer')}
          </p>
        ) : null}

        <label className="lm-field">
          <span className="lm-field__label">{t('expenses.allocationLabel')}</span>
          <select
            className="lm-input"
            name="allocation"
            value={allocation}
            onChange={(e) => {
              setAllocation(e.currentTarget.value as ExpenseAllocation)
            }}
          >
            {EXPENSE_ALLOCATIONS.map((id) => (
              <option key={id} value={id}>
                {t(ALLOCATION_LABEL[id])}
              </option>
            ))}
          </select>
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('expenses.dateLabel')}</span>
          <input
            className="lm-input"
            name="incurredOn"
            placeholder="2026-09-30"
            autoComplete="off"
            value={dateInput}
            onChange={(e) => {
              setDateInput(e.currentTarget.value)
            }}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('expenses.noteLabel')}</span>
          <input
            className="lm-input"
            name="note"
            autoComplete="off"
            value={note}
            onChange={(e) => {
              setNote(e.currentTarget.value)
            }}
          />
        </label>

        <div className="lm-screen__actions">
          <button type="submit" className="lm-button lm-button--primary">
            {t('expenses.submit')}
          </button>
        </div>

        {error === null ? null : (
          <p className="lm-error" role="alert">
            {error}
          </p>
        )}
        {line === null ? null : (
          <p className="lm-queue-state" role="status">
            {line}
          </p>
        )}
      </form>

      <h2 className="lm-screen__subheading">{t('expenses.listHeading')}</h2>
      {expenses.length === 0 ? (
        <p className="lm-hint">{t('expenses.empty')}</p>
      ) : (
        <ul data-testid="expenses-list">
          {expenses.map((row) => (
            <li key={row.id} data-expense-id={row.id}>
              <span>{t(CATEGORY_LABEL[row.category])}</span>
              {' · '}
              <Ltr>{formatWireForDisplay(row.amount.minor, row.amount.currency)}</Ltr>
              {' · '}
              <Ltr>{formatDay(row.incurredOn, timezone, locale)}</Ltr>
              {' · '}
              <span>{t(PAID_FROM_LABEL[row.paidFrom])}</span>
              {row.branchId === null ? (
                <>
                  {' · '}
                  <span className="lm-badge">{t('expenses.shared')}</span>
                </>
              ) : null}
              {row.note === null ? null : (
                <>
                  {' · '}
                  <span>{row.note}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
